import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Coins, Target, Note, Heart, Clock, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Compact on-video overlay for stream apps, styled after the industry-standard
 * cam-site layout (Lovense/Chaturbate reference from user):
 *
 *   Top-left column (stacked):
 *     [Tip Menu — 2-col grid of tiny cards]
 *     [Top Tippers — 1-col compact list w/ heart icon]
 *
 *   Top-right corner:
 *     [Tip Goal — small progress pill]
 *
 *   Top-center (dismissible):
 *     [Notice banner]
 *
 *   Bottom-left:
 *     [Countdown pill]
 *
 * Root uses `absolute inset-0 pointer-events-none z-20` so it lays over the
 * video without blocking non-interactive space. Individual widgets opt into
 * pointer events with `pointer-events-auto` on themselves.
 *
 * Props:
 *   streamId, onTip, leaderboard, tokensReceived
 *   compact (bool) — mini-mode for PIP thumbnails; hides text-heavy widgets
 */
const EdgeStreamAppsOverlay = ({ streamId, onTip, leaderboard, tokensReceived, compact = false }) => {
  const [apps, setApps] = useState([]);
  const [noticeDismissed, setNoticeDismissed] = useState(false);

  useEffect(() => {
    if (!streamId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/stream-apps/stream/${streamId}`, { withCredentials: true });
        if (!cancelled) setApps(res.data.apps || []);
      } catch { /* silent — polling continues */ }
    };
    load();
    const t = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, [streamId]);

  const tipMenu = apps.find(a => a.app_key === 'tip_menu');
  const tipGoal = apps.find(a => a.app_key === 'tip_goal');
  const notice = apps.find(a => a.app_key === 'notice');
  const topTippers = apps.find(a => a.app_key === 'top_tippers');
  const countdown = apps.find(a => a.app_key === 'countdown');

  if (apps.length === 0) return null;

  return (
    <div className="pointer-events-none absolute inset-0 z-20" data-testid="edge-stream-apps-overlay">
      {/* ═══════ TOP-LEFT COLUMN: Tip Menu grid + Top Tippers list ═══════ */}
      {(tipMenu || (topTippers && leaderboard?.length > 0)) && (
        <div className={`absolute top-2 left-2 pointer-events-auto space-y-1.5 ${compact ? 'max-w-[110px]' : 'max-w-[200px]'}`}>
          {/* Tip Menu — 2-column compact grid of tiny cards */}
          {tipMenu && onTip && (
            <div
              className="rounded-lg bg-black/55 backdrop-blur-sm border border-white/10 p-1.5 shadow-lg"
              data-testid="edge-tip-menu-grid"
            >
              <div className="text-[9px] font-bold uppercase tracking-wider text-blue-300 mb-1 px-1 flex items-center gap-1">
                <Heart weight="fill" className="w-2.5 h-2.5" />
                {tipMenu.config.title || 'Tip Menu'}
              </div>
              <div className={`grid ${compact ? 'grid-cols-1' : 'grid-cols-2'} gap-1`}>
                {(tipMenu.config.items || []).slice(0, compact ? 3 : 8).map((it, i) => (
                  <button
                    key={`${it.label}-${it.amount}-${i}`}
                    onClick={async () => {
                      try {
                        await onTip(it.amount, it.label);
                        toast.success(`Tipped ${it.amount} for "${it.label}"`);
                      } catch { /* parent handles */ }
                    }}
                    className="group flex flex-col items-center justify-center px-1.5 py-1 rounded-md bg-indigo-900/40 hover:bg-indigo-700/60 border border-indigo-400/20 hover:border-indigo-300/50 transition-colors"
                    data-testid={`edge-tip-item-${i}`}
                    title={`${it.label} — ${it.amount} tokens`}
                  >
                    <span className="flex items-center gap-0.5 text-white font-bold text-xs leading-tight">
                      {it.amount}
                      <Coins weight="fill" className="w-2 h-2 text-amber-300" />
                    </span>
                    <span className="text-[8px] text-white/70 truncate max-w-full leading-tight">
                      {it.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Top Tippers — compact 1-col list */}
          {topTippers && leaderboard?.length > 0 && !compact && (
            <div
              className="rounded-lg bg-black/55 backdrop-blur-sm border border-white/10 p-1.5 shadow-lg"
              data-testid="edge-top-tippers"
            >
              <div className="text-[9px] font-bold uppercase tracking-wider text-amber-300 mb-1 px-1">
                Top Tippers
              </div>
              <div className="space-y-0.5">
                {leaderboard.slice(0, topTippers.config.limit || 5).map(t => (
                  <div key={t.user_id} className="flex items-center gap-1 text-[10px] leading-tight">
                    <Heart weight="fill" className="w-2.5 h-2.5 text-indigo-400 flex-shrink-0" />
                    <span className="text-white truncate flex-1">{t.display_name}</span>
                    <span className="text-amber-300 font-bold whitespace-nowrap">{t.total_amount}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════ TOP-RIGHT: Tip Goal ═══════ */}
      {tipGoal && (
        <div className={`absolute top-2 right-2 pointer-events-auto ${compact ? 'text-[9px]' : 'text-xs'}`} data-testid="edge-tip-goal">
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/55 backdrop-blur-sm border border-blue-500/30 shadow-lg">
            <Target weight="fill" className="w-3 h-3 text-blue-400 flex-shrink-0" />
            <span className="text-white/90 font-medium truncate max-w-[100px]">
              {tipGoal.config.label || 'Goal'}
            </span>
            <span className="text-amber-300 font-bold whitespace-nowrap">
              {tokensReceived}/{tipGoal.config.amount}
            </span>
            <div className={`${compact ? 'w-8' : 'w-12'} h-1 bg-slate-800/80 rounded-full overflow-hidden`}>
              <div
                className="h-full bg-gradient-to-r from-amber-400 to-blue-500 transition-all"
                style={{ width: `${Math.min(100, (tokensReceived / (tipGoal.config.amount || 1)) * 100)}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ═══════ TOP-CENTER: Dismissible Notice ═══════ */}
      <AnimatePresence>
        {notice && !noticeDismissed && !compact && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="absolute top-2 left-1/2 -translate-x-1/2 pointer-events-auto max-w-[55%]"
            data-testid="edge-notice"
          >
            <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/95 text-black shadow-lg text-xs">
              <Note weight="fill" className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="font-medium truncate">
                {notice.config.title ? `${notice.config.title}: ` : ''}{notice.config.body}
              </span>
              <button
                onClick={() => setNoticeDismissed(true)}
                className="hover:bg-black/10 rounded-full p-0.5"
                aria-label="Dismiss"
                data-testid="edge-notice-dismiss"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══════ BOTTOM-LEFT: Countdown ═══════ */}
      {countdown && countdown.config.ends_at && (
        <div className="absolute bottom-2 left-2 pointer-events-auto" data-testid="edge-countdown">
          <CompactCountdown label={countdown.config.label} endsAt={countdown.config.ends_at} compact={compact} />
        </div>
      )}
    </div>
  );
};

const CompactCountdown = ({ label, endsAt, compact }) => {
  const [remaining, setRemaining] = useState('');
  useEffect(() => {
    const tick = () => {
      const ms = new Date(endsAt).getTime() - Date.now();
      if (ms <= 0) { setRemaining('00:00'); return; }
      const total = Math.floor(ms / 1000);
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      setRemaining(h > 0 ? `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}` : `${m}:${String(s).padStart(2,'0')}`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [endsAt]);
  return (
    <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/55 backdrop-blur-sm border border-blue-500/30 shadow-lg text-xs">
      <Clock weight="fill" className="w-3 h-3 text-blue-400 flex-shrink-0" />
      {label && !compact && <span className="text-white/80 truncate max-w-[80px]">{label}</span>}
      <span className="text-white font-mono font-bold">{remaining}</span>
    </div>
  );
};

export default EdgeStreamAppsOverlay;
